import { computed, onBeforeUnmount } from 'vue';
import InitialState from '@/InitialState';
import { confettiPalettes, useConfetti } from '@/Composables/useConfetti';
import { useEffectsPreference } from '@/Composables/useEffectsPreference';

/** The occasions a screen can ask to celebrate. */
export const celebrationOccasions = {
    pledgeSuccess: 'pledgeSuccess',
    richTextMarker: 'richTextMarker',
    specialUser: 'specialUser',
    projectFunded: 'projectFunded',
    campaignEnded: 'campaignEnded',
    campaignEndedInEndgame: 'campaignEndedInEndgame',
};

// The shower starts once the crossfire reaches the top. 0.08 rather than 0, because a shot aimed
// at the edge peaks just below it. The timeout starts the shower anyway if the crossfire never
// gets that high, e.g. when slowed by reduced motion or on a very tall viewport.
const reachedTop = { above: 0.08, orAfter: 2500 };

/** Emissions per occasion, in order; `after` delays an emission until the previous one arrives. */
const celebrations = {
    [celebrationOccasions.pledgeSuccess]: [
        { preset: 'celebrate-cannons', stream: false, palette: confettiPalettes.platform },
    ],
    [celebrationOccasions.richTextMarker]: [
        { preset: 'celebrate-crossfire', stream: false, palette: confettiPalettes.platform },
        { preset: 'celebrate-shower', stream: true, palette: confettiPalettes.platform, after: reachedTop },
    ],
    [celebrationOccasions.specialUser]: [
        { preset: 'celebrate-forever', stream: true, palette: confettiPalettes.platform },
    ],
    [celebrationOccasions.projectFunded]: [
        { preset: 'celebrate-cannons', stream: false, palette: confettiPalettes.platform },
    ],
    [celebrationOccasions.campaignEnded]: [
        { preset: 'celebrate-cannons', stream: false, palette: confettiPalettes.platform },
    ],
    [celebrationOccasions.campaignEndedInEndgame]: [
        { preset: 'celebrate-cannons', stream: false, palette: confettiPalettes.endgame },
    ],
};

/**
 * Whether the platform runs celebrations at all. Unlike canCelebrate, it ignores this visitor's
 * own preference: a creator who turned effects off still writes for readers who did not.
 */
export const isCelebrationFeatureEnabled = () => !!InitialState.featureFlags?.celebratoryEffectsEnabled;

export const useCelebrate = () => {
    const { isEffectsDisabled } = useEffectsPreference();
    const confetti = useConfetti();
    // The stage's arrival watch cannot be cancelled, so late work checks that it is still wanted.
    const pending = [];
    let abandoned = false;

    const canCelebrate = computed(() => isCelebrationFeatureEnabled() && !isEffectsDisabled.value);

    /** Named in the settings, resolved on the server so no page carries the list of addresses. */
    const isSpecialUser = computed(() =>
        canCelebrate.value && !!InitialState.featureFlags?.celebratoryEffectsSpecialUser);

    /**
     * Runs the effect for an occasion, or does nothing when effects are off.
     * @param {string} occasion One of celebrationOccasions.
     */
    const celebrate = (occasion) => {
        const celebration = celebrations[occasion];

        if (!celebration) {
            throw new Error(`useCelebrate: unknown occasion "${ occasion }"`);
        }

        if (!canCelebrate.value) {
            return;
        }

        abandoned = false;

        celebration.forEach((effect) => {
            let done = false;
            const run = () => {
                if (done || abandoned) {
                    return;
                }

                done = true;

                if (effect.stream) {
                    confetti.stream(effect.preset, { papers: effect.palette });
                }
                else {
                    confetti.burst(effect.preset, { papers: effect.palette });
                }
            };

            if (effect.after) {
                confetti.onceAboveY(effect.after.above, run);
                pending.push(setTimeout(run, effect.after.orAfter));
            }
            else {
                run();
            }
        });
    };

    const forget = () => {
        abandoned = true;
        pending.forEach(clearTimeout);
        pending.length = 0;
    };

    onBeforeUnmount(forget);

    return {
        canCelebrate,
        isSpecialUser,
        celebrate,
        stopCelebrating: () => {
            forget();
            confetti.stop({ clear: true });
        },
    };
};
